/* Utilidades puras y reutilizables para Teledetección */
import { FormGroup } from '@angular/forms';
import {
  COLOR_RAMPS,
  INDEX_TO_RAMP,
  getExpandedRamp,
} from '../components/colored-scale/color-ramps';

// Leaflet es global en esta app
declare const L: any;

/** Verifica si el mapa Leaflet está listo (panes creados) */
export function isLeafletMapReady(map: any | null | undefined): boolean {
  if (!map) return false;
  try {
    const panes = map.getPanes?.();
    return !!(panes?.overlayPane && panes?.tilePane);
  } catch {
    return false;
  }
}

/** Espera a que una condición sea verdadera (polling simple) */
export function waitForLeafletMapReady(
  check: () => boolean,
  intervalMs = 50
): Promise<boolean> {
  return new Promise(resolve => {
    const tick = () => (check() ? resolve(true) : setTimeout(tick, intervalMs));
    tick();
  });
}

/** Asegura que un LayerGroup tenga una capa base (si está vacío) */
export function ensureLayerGroupHasBase(group: any, baseLayer: any): void {
  if (!group || !baseLayer) return;
  try {
    if (group.getLayers?.().length === 0) {
      group.addLayer(baseLayer);
    }
  } catch {}
}

/** Prepara grupos de split con una capa base por lado */
export function prepareSplitGroups(
  leftGroup: any,
  rightGroup: any,
  baseFactory: () => any
): void {
  try {
    leftGroup?.clearLayers?.();
    rightGroup?.clearLayers?.();
    const leftBase = baseFactory?.();
    const rightBase = baseFactory?.();
    if (leftBase) leftGroup?.addLayer?.(leftBase);
    if (rightBase) rightGroup?.addLayer?.(rightBase);
  } catch {}
}

/** Normaliza el valor de fecha a 'YYYY-MM-DD' */
export function extractDateString(fecha: any): string | null {
  if (!fecha) return null;
  try {
    if (typeof fecha === 'string') {
      return fecha.length >= 10 ? fecha.substring(0, 10) : fecha;
    }
    if (fecha instanceof Date) {
      return fecha.toISOString().slice(0, 10);
    }
    if (fecha?.toDate) {
      return new Date(fecha.toDate()).toISOString().slice(0, 10);
    }
    return new Date(fecha).toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

/** Extrae el nombre/código del índice desde el control (objeto o string) */
export function extractIndiceValue(indice: any): string | null {
  if (!indice) return null;
  if (typeof indice === 'string') return indice;
  return indice?.value ?? null;
}

/** Obtiene la rampa (expandida opcional) apropiada para un índice */
export function getColorRampForIndexName(
  indexName: string,
  expanded = false
): string[] {
  const rampKey = INDEX_TO_RAMP[indexName] || 'RdYlGn';
  return expanded ? getExpandedRamp(rampKey) : COLOR_RAMPS[rampKey];
}

/** Función de color para un valor escalar [min,max] sobre una rampa */
export function buildColorFn(ramp: string[], min: number, max: number) {
  const n = ramp.length - 1;
  const span = max - min || 1;
  return (valueArr: number[] | number) => {
    const v = Array.isArray(valueArr) ? valueArr[0] : valueArr;
    if (v == null || isNaN(v as any)) return null;
    const clamped = Math.min(Math.max(v as number, min), max);
    const t = (clamped - min) / span;
    const idx = Math.round(t * n);
    return ramp[idx] || ramp[ramp.length - 1];
  };
}

/** Convierte un data URI base64 a ArrayBuffer */
export function base64ToArrayBuffer(dataUri: string): ArrayBuffer {
  const base64 = dataUri.split(',')[1] || dataUri;
  const decoder = globalThis?.atob;
  if (typeof decoder !== 'function') {
    throw new Error('No hay decodificador base64 disponible en este entorno');
  }
  const binary = decoder(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/** Crea un GeoRasterLayer a partir de un TIFF base64 */
export async function createGeoRasterLayerFromBase64(
  base64: string,
  bounds: any,
  colorFn?: ((val: number[] | number) => string | null) | undefined,
  extraOptions?: any,
  maskGeojson?: any
): Promise<any | null> {
  try {
    const arrayBuffer = base64ToArrayBuffer(base64);
    const parseGeoraster = (await import('georaster')).default;
    const GeoRasterLayer = (await import('georaster-layer-for-leaflet'))
      .default as any;
    const georaster = await parseGeoraster(arrayBuffer);

    // Si no hay colorFn, renderizar imagen directamente (para VISIBLE/Imagen real)
    const layerOptions: any = {
      georaster,
      opacity: 1,
      resolution: 256,
      resampleMethod: 'bilinear',
      bounds,
      ...(extraOptions || {}),
    };

    // Solo aplicar pixelValuesToColorFn si está definido
    if (colorFn) {
      layerOptions.pixelValuesToColorFn = colorFn;
    }

    // Para VISIBLE/Imagen real, agregar función que haga transparentes los píxeles negros
    // que están FUERA del área de interés (el backend rellena con negro las áreas externas)
    if (maskGeojson && !colorFn) {
      console.log(
        '🎭 Aplicando máscara al GeoRasterLayer:',
        JSON.stringify(maskGeojson)
      );
      console.log('📍 Proyección del georaster:', georaster.projection);
      console.log('🔢 NoData value del georaster:', georaster.noDataValue);

      // Usar pixelValuesToColorFn para hacer transparentes los píxeles negros
      // que corresponden a áreas fuera del polígono
      layerOptions.pixelValuesToColorFn = (values: number[]) => {
        // Si es una imagen RGB (3 bandas)
        if (values.length >= 3) {
          const [r, g, b] = values;

          // Si el píxel es negro o muy oscuro (valores bajos en todas las bandas),
          // asumimos que está fuera del área y lo hacemos transparente
          // Umbral: si todas las bandas son < 10, considerarlo como "fuera del área"
          if (r < 10 && g < 10 && b < 10) {
            return null; // Transparente
          }

          // Píxeles normales dentro del área
          return `rgb(${r},${g},${b})`;
        }

        return null;
      };
    }

    const layer = new GeoRasterLayer(layerOptions);
    return layer;
  } catch (e) {
    console.error('❌ Error creando GeoRasterLayer:', e);
    return null;
  }
}

/** Calcula bounds a partir del response del backend (bbox o geometry) */
export function computeBoundsFromResponse(data: any): any | null {
  const bbox = data?.bbox;
  if (
    bbox &&
    typeof bbox.west === 'number' &&
    typeof bbox.south === 'number' &&
    typeof bbox.east === 'number' &&
    typeof bbox.north === 'number'
  ) {
    return L.latLngBounds([bbox.south, bbox.west], [bbox.north, bbox.east]);
  }
  if (data?.geometry) {
    try {
      const tmp = L.geoJSON(data.geometry);
      const b = tmp.getBounds();
      try {
        tmp.remove();
      } catch {}
      return b;
    } catch {}
  }
  return null;
}

/** Recalcula estilos de áreas para ocultar geometrías con overlay activo */
export function recomputeAreaStyles(
  areaLayers: Array<{ id: number; layer: any }>,
  overlaysBySide: { [side: string]: Map<number, any> }
): void {
  const activeIds = new Set<number>();
  Object.values(overlaysBySide).forEach(m =>
    m.forEach((_, id) => activeIds.add(id))
  );
  const defaultStyle = {
    color: '#FFFFFF',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.8,
  };
  const hiddenStyle = {
    color: 'transparent',
    weight: 0,
    opacity: 0,
    fillOpacity: 0,
  } as any;
  areaLayers.forEach(a => {
    try {
      a.layer.setStyle(activeIds.has(a.id) ? hiddenStyle : defaultStyle);
    } catch {}
  });
}

/** Resuelve el área activa para la leyenda */
export function resolveActiveAreaIdForLegend(
  overlaysSideMap: Map<number, any>,
  selectedId: number | null | undefined
): number | null {
  if (selectedId != null && overlaysSideMap.has(selectedId)) return selectedId;
  const it = overlaysSideMap.keys();
  const next = it.next();
  return next && !next.done ? (next.value as number) : null;
}

/** Lee 'general' | 'especifica' desde un FormGroup de filtros */
export function getEscalaTypeFromForm(
  form: FormGroup | null | undefined
): 'general' | 'especifica' {
  const val = (form as any)?.controls?.['escala']?.value?.value;
  return val === 'especifica' ? 'especifica' : 'general';
}

/** Obtiene rango de escala desde el formulario (o por defecto) */
export function getScaleRangeFromForm(
  form: FormGroup | null | undefined,
  defaults = { min: -1, max: 1 }
): { min: number; max: number } {
  const min = Number(
    (form as any)?.controls?.['escala_min']?.value ?? defaults.min
  );
  const max = Number(
    (form as any)?.controls?.['escala_max']?.value ?? defaults.max
  );
  return { min, max };
}
